// F-009: VigilAfrica Landing Page
// Spec: product.md §4 F-009
// Acceptance criteria:
//   - VigilAfrica name and tagline rendered
//   - No Vite/React template content
//   - "early prototype" status notice visible
//   - Links to GitHub repository
//   - Responsive at 375px and 1280px
//   - No API calls (static component only)
import { Suspense, lazy } from 'react'
import {
  Satellite,
  Map,
  MapPin,
  Building2,
  Newspaper,
  Truck,
  ShieldAlert,
  AlertTriangle,
  Droplet,
  Flame,
  ArrowRight,
  Check,
  Loader,
} from 'lucide-react'

import { BrowserRouter as Router, Routes, Route, Link } from 'react-router'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { BrandMark } from './components/BrandMark'
import { LoadingState } from './components/LoadingState'
import { useReveal } from './hooks/useReveal'
import './App.css'
import MILESTONES from './data/milestones.json'

// PageError is the fallback rendered when any descendant of the ErrorBoundary
// throws during render. The previous implementation used useRouteError() from
// react-router, which only fires under the data-router (createBrowserRouter)
// API — under the JSX <Routes>+<Route> setup we use, errorElement is silently
// ignored, so the fallback never rendered. chore-post-v11-quality-sweep F2.
function PageError({ error }: FallbackProps) {
  const message = error instanceof Error ? error.message : 'Something went wrong. Please try again.'
  return (
    <div className="container section" role="alert">
      <h2>Something went wrong</h2>
      <p>{message}</p>
      <a href="/" className="btn btn-outline">← Back to Dashboard</a>
    </div>
  )
}

const GithubIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
    <path d="M9 18c-4.51 2-5-2-7-2" />
  </svg>
)

const GITHUB_URL = 'https://github.com/didi-rare/vigilafrica'

const EventsDashboard = lazy(async () => {
  const module = await import('./components/EventsDashboard')
  return { default: module.EventsDashboard }
})

const EventDetail = lazy(async () => {
  const module = await import('./pages/EventDetail')
  return { default: module.EventDetail }
})

const ForPartners = lazy(async () => {
  const module = await import('./pages/ForPartners')
  return { default: module.ForPartners }
})
const STEPS = [
  {
    icon: <Satellite size={20} />,
    title: 'Poll',
    desc: 'Fetches natural event data from NASA\u2019s EONET feed \u2014 floods and wildfires, filtered to Nigeria and Ghana.',
  },
  {
    icon: <Map size={20} />,
    title: 'Enrich',
    desc: 'Maps raw coordinates to familiar names — "Benue State, Nigeria" instead of [8.13, 7.33].',
  },
  {
    icon: <MapPin size={20} />,
    title: 'Serve',
    desc: 'Delivers localised event data through a REST API and an interactive map experience.',
  },
]

const AUDIENCE = [
  { icon: <Building2 size={18} />, label: 'NGO Field Teams',     desc: 'Situational awareness without geospatial expertise' },
  { icon: <Newspaper size={18} />, label: 'Local Journalists',   desc: 'Verify event locations by state name, not coordinates' },
  { icon: <Truck size={18} />,     label: 'Logistics Planners',  desc: 'Assess route risk from active flood and wildfire events' },
  { icon: <ShieldAlert size={18} />, label: 'Civic Responders',   desc: 'Community preparedness and local response planning' },
]

function StagingBanner() {
  if (import.meta.env.VITE_ENV !== 'staging') return null
  return (
    <div
      id="staging-banner"
      className="staging-banner"
      role="note"
      aria-label="Test environment notice"
    >
      <AlertTriangle className="staging-banner__icon" size={16} aria-hidden="true" />
      <span>Staging environment — pre-release/test data. Do not rely on this for operational decisions.</span>
    </div>
  )
}

// DashboardFallback is on screen for exactly as long as the lazy dashboard chunk
// is pending (chore-web-audit-leftovers items 3–5).
//
// `.dashboard-fallback__progress` is a fixed 3px bar across the top of the
// viewport. It exists because at 375x812 the in-flow fallback starts at y≈1098
// — below the fold — so without it a phone user on a slow connection sees the
// hero and nothing that says anything is loading. Fixed positioning means no
// layout footprint and therefore no CLS contribution at any viewport. It is
// aria-hidden: the LoadingState live region carries the announcement.
//
// `dashboard-fallback` reserves viewport height while the chunk loads. Without
// it the fallback is ~26px and the mounted dashboard is ~1459px (desktop), so
// mounting shoves everything below it — `#how-it-works`, visible at the bottom
// of the viewport — clean off the screen. Measured CLS 0.262 at 1350x940, 0.082
// at 768x1024. See fix-dashboard-layout-reservation for the trial data.
function DashboardFallback() {
  return (
    <>
      <div className="dashboard-fallback__progress" aria-hidden="true" />
      <div className="container section dashboard-fallback">
        <LoadingState message="Loading dashboard telemetry..." />
      </div>
    </>
  )
}

function App() {
  // Scroll-reveal refs for the landing content sections (fade-up on enter,
  // reduced-motion-safe via the hook). Hooks are called unconditionally here;
  // refs stay null on routes where these sections aren't rendered.
  const { ref: stepsRef, revealed: stepsShown } = useReveal<HTMLElement>()
  const { ref: audienceRef, revealed: audienceShown } = useReveal<HTMLElement>()
  const { ref: statusRef, revealed: statusShown } = useReveal<HTMLElement>()
  return (
    <Router>
      <ErrorBoundary
        FallbackComponent={PageError}
        onError={(err) => {
          // Keep render-time errors visible in the console for local debugging.
          console.error('[app] caught render error:', err)
        }}
      >
      <div id="app">
      <a href="#main" className="skip-link">Skip to main content</a>
      <StagingBanner />

      {/* ── Navigation ── */}
      <nav className="nav" aria-label="Main navigation">
        <a className="nav-logo" href="/" aria-label="VigilAfrica home">
          <BrandMark size={30} className="brand-mark" />
          <span className="logo-text">VigilAfrica</span>
          <span className="nav-station" aria-hidden="true">NG·GH</span>
        </a>
        <div className="nav-actions">
          <Link id="nav-partners-link" to="/for-partners" className="nav-link">
            For partners
          </Link>
          <a
            id="nav-github-link"
            href={GITHUB_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline"
            aria-label="View VigilAfrica on GitHub"
          >
            <GithubIcon />
            <span>View on GitHub</span>
          </a>
        </div>
      </nav>

      <main id="main">
        <Routes>
          <Route path="/" element={
            <>
              <section id="hero" className="hero" aria-labelledby="hero-heading">
                <div className="hero__graticule" aria-hidden="true" />

                <div className="container hero__inner">
                  <div className="hero__lead">
                    <p className="hero__readout">
                      <span className="signal-dot" aria-hidden="true" />
                      <span className="hero__readout-key">LIVE&nbsp;FEED</span>
                      <span className="hero__readout-sep" aria-hidden="true">/</span>
                      <span className="hero__readout-coord">09°04′N&nbsp;07°29′E</span>
                      <span className="hero__readout-sep" aria-hidden="true">/</span>
                      <span>NASA&nbsp;EONET</span>
                    </p>

                    <h1 id="hero-heading" className="hero-title">
                      What is happening<span className="hero-title__accent"> near you?</span>
                    </h1>

                    <p className="hero-desc">
                      VigilAfrica translates raw NASA satellite event data into local African
                      context — floods and wildfires by country and state, not just coordinates.
                      Open-source. Nigeria and Ghana live.
                    </p>

                    <div className="hero-cta">
                      <a id="hero-explore-cta" href="#dashboard" className="btn btn-primary">
                        Explore latest events
                        <ArrowRight size={18} aria-hidden="true" className="btn__arrow" />
                      </a>
                      <a
                        id="hero-github-cta"
                        href={GITHUB_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline"
                      >
                        Contribute on GitHub
                      </a>
                    </div>
                  </div>

                  <div className="hero__panel">
                    <div className="hero__panel-head">
                      <span className="mono-label">FEED&nbsp;PARAMETERS</span>
                      <span className="signal-dot signal-dot--sm" aria-hidden="true" />
                    </div>
                    <dl className="hero__telemetry">
                      <div className="hero__telemetry-row">
                        <dt>Source</dt>
                        <dd>NASA EONET</dd>
                      </div>
                      <div className="hero__telemetry-row">
                        <dt>Region</dt>
                        <dd>Nigeria · Ghana</dd>
                      </div>
                      <div className="hero__telemetry-row">
                        <dt>Tracking</dt>
                        <dd className="hero__tracking">
                          <span className="tag tag--flood">
                            <Droplet size={13} aria-hidden="true" /> Floods
                          </span>
                          <span className="tag tag--fire">
                            <Flame size={13} aria-hidden="true" /> Wildfires
                          </span>
                        </dd>
                      </div>
                      <div className="hero__telemetry-row">
                        <dt>License</dt>
                        <dd>Apache-2.0</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </section>

              <Suspense fallback={<DashboardFallback />}>
                <EventsDashboard />
              </Suspense>

              <section
                ref={stepsRef}
                id="how-it-works"
                className={`how-it-works reveal${stepsShown ? ' is-revealed' : ''}`}
                aria-labelledby="how-heading"
              >
                <div className="container">
                  <span className="section-label">Architecture</span>
                  <h2 id="how-heading" className="section-title">How it works</h2>
                  <p className="section-subtitle">
                    A simple three-stage pipeline turns satellite metadata into something any field
                    coordinator can understand.
                  </p>

                  <ul className="steps" role="list">
                    {STEPS.map((step, i) => (
                        <li key={step.title} className="step">
                          <div className="step-head">
                            <div className="step-icon" aria-hidden="true">{step.icon}</div>
                            <span className="step-index" aria-hidden="true">
                              {String(i + 1).padStart(2, '0')}
                            </span>
                          </div>
                          <h3>{step.title}</h3>
                          <p>{step.desc}</p>
                        </li>
                    ))}
                  </ul>
                </div>
              </section>

              <section
                ref={audienceRef}
                id="built-for"
                className={`built-for reveal${audienceShown ? ' is-revealed' : ''}`}
                aria-labelledby="built-heading"
              >
                <div className="container">
                  <span className="section-label">Use cases</span>
                  <h2 id="built-heading" className="section-title">Built for people on the ground</h2>
                  <p className="section-subtitle">
                    Not for data scientists — for the people who need to act on events quickly.
                  </p>

                  <div className="audience-grid">
                    {AUDIENCE.map((item) => (
                      <article key={item.label} className="audience-card">
                        <div className="audience-icon" aria-hidden="true">{item.icon}</div>
                        <h3>{item.label}</h3>
                        <p>{item.desc}</p>
                      </article>
                    ))}
                  </div>
                </div>
              </section>

              <section
                ref={statusRef}
                id="roadmap"
                className={`status reveal${statusShown ? ' is-revealed' : ''}`}
                aria-labelledby="status-heading"
              >
                <div className="container">
                  <div className="status-card glass-effect">
                    <div className="status-header">
                      <span className="status-dot" aria-hidden="true" />
                      <span>Project Status</span>
                    </div>

                    <h2 id="status-heading" className="section-title" style={{ marginBottom: '12px' }}>
                      Building in the open — Nigeria &amp; Ghana live
                    </h2>

                    <p>
                      VigilAfrica is being built milestone by milestone. v1.0 (Credible Public Launch)
                      shipped — Nigeria and Ghana run end-to-end in production — followed by v1.1 and
                      v1.2, which hardened release automation and the country boundary data behind the
                      enrichment pipeline. v1.3 (category expansion + design-system tokens) is the next
                      milestone. Staging and production both deploy continuously.
                    </p>

                    <nav aria-label="Milestone progress">
                      <ul className="milestone-list">
                        {MILESTONES.map((m) => (
                          <li
                            key={m.label}
                            className={`milestone${m.active ? ' milestone--active' : ''}${m.complete ? ' milestone--complete' : ''}`}
                            aria-current={m.active ? 'step' : undefined}
                          >
                            {m.label}
                            {m.complete && (
                              <>
                                {' '}
                                <span className="milestone-tag milestone-tag--complete">
                                  <Check size={12} aria-hidden="true" /> Complete
                                </span>
                              </>
                            )}
                            {m.active && (
                              <>
                                {' '}
                                <span className="milestone-tag">
                                  <Loader size={12} aria-hidden="true" /> In progress
                                </span>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    </nav>

                    <a
                      id="status-github-cta"
                      href={GITHUB_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      Follow progress on GitHub →
                    </a>
                  </div>
                </div>
              </section>
            </>
          } />
          <Route
            path="/events/:id"
            element={
              <Suspense fallback={<div className="container section">Loading event telemetry...</div>}>
                <EventDetail />
              </Suspense>
            }
          />
          <Route
            path="/for-partners"
            element={
              <Suspense fallback={<div className="container section">Loading...</div>}>
                <ForPartners />
              </Suspense>
            }
          />
        </Routes>
      </main>

      {/* ── Footer ── */}
      <footer className="footer" role="contentinfo">
        <div className="container">
          <p className="footer-disclaimer">
            Awareness tool — not an official emergency alert system.
          </p>
          <p className="footer-sub">
            Sources: NASA EONET ·{' '}
            <a
              href={`${GITHUB_URL}/releases/latest`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Latest release
            </a>{' '}
            ·{' '}
            <a
              href={`${GITHUB_URL}/blob/main/openspec/specs/vigilafrica/roadmap.md`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Roadmap
            </a>{' '}
            ·{' '}
            <a href={`${GITHUB_URL}/issues`} target="_blank" rel="noopener noreferrer">
              GitHub Issues
            </a>{' '}
            ·{' '}
            <a
              href="https://www.apache.org/licenses/LICENSE-2.0"
              target="_blank"
              rel="noopener noreferrer"
            >
              Apache 2.0
            </a>
          </p>
        </div>
      </footer>
      </div>
      </ErrorBoundary>
    </Router>
  )
}

export default App



